import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/hooks/use-toast";
import { inboxKeys } from "./queryKeys";
import type { EntityMailboxType } from "./useEntityConversations";

export interface SavedMessage {
	id: string;
	created_at: string;
	message_type?: string;
	subject?: string;
	message: string;
	participants?: unknown;
	original_message_key?: string;
	added_by_user: string;
	added_by_profile?: {
		first_name?: string;
		last_name?: string;
		email?: string;
	};
	linkedCandidates: Array<{ id: string; name: string }>;
	linkedJobs: Array<{ id: string; title: string }>;
	linkedCompanies: Array<{ id: string; name: string }>;
}

const BASE_SELECT = `
	*,
	added_by_profile:profiles!public_messages_added_by_user_fkey(first_name, last_name, email)
`;

function buildSelect(innerLink: "candidate" | "job" | "company"): string {
	return `${BASE_SELECT},
		linkedCandidates:public_message_candidate${innerLink === "candidate" ? "!inner" : ""}(
			candidate:candidates(id, name)
		),
		linkedJobs:public_message_jobs${innerLink === "job" ? "!inner" : ""}(
			job:jobs(id, title)
		),
		linkedCompanies:public_message_company${innerLink === "company" ? "!inner" : ""}(
			company:companies(id, name)
		)`;
}

async function fetchSavedMessages(
	entityType: EntityMailboxType,
	entityId: string,
	organizationId: string
): Promise<SavedMessage[]> {
	let query;
	if (entityType === "candidate") {
		query = supabase
			.from("public_messages")
			.select(buildSelect("candidate"))
			.eq("organization_uuid", organizationId)
			.eq("public_message_candidate.candidate_id", entityId);
	} else if (entityType === "job") {
		query = supabase
			.from("public_messages")
			.select(buildSelect("job"))
			.eq("organization_uuid", organizationId)
			.eq("public_message_jobs.job_id", entityId);
	} else if (entityType === "company") {
		query = supabase
			.from("public_messages")
			.select(buildSelect("company"))
			.eq("organization_uuid", organizationId)
			.eq("public_message_company.company_id", entityId);
	} else {
		return [];
	}

	const { data, error } = await query.order("created_at", { ascending: false });
	if (error) throw error;

	// Flatten the link-table rows to plain entity arrays.
	return (data || []).map((message: Record<string, unknown>) => ({
		...(message as unknown as SavedMessage),
		linkedCandidates:
			(message.linkedCandidates as Array<{ candidate: { id: string; name: string } }> | null)
				?.map((link) => link.candidate)
				.filter(Boolean) || [],
		linkedJobs:
			(message.linkedJobs as Array<{ job: { id: string; title: string } }> | null)
				?.map((link) => link.job)
				.filter(Boolean) || [],
		linkedCompanies:
			(message.linkedCompanies as Array<{ company: { id: string; name: string } }> | null)
				?.map((link) => link.company)
				.filter(Boolean) || [],
	}));
}

/** Saved messages linked to a CRM entity (org-scoped `public_messages`). */
export function useSavedMessages(entityType: EntityMailboxType, entityId: string) {
	const { currentOrganization } = useAuth();
	const organizationId = currentOrganization?.id;
	// Match linking isn't modeled in the link tables yet.
	const isSupported = entityType !== "match";

	const query = useQuery({
		queryKey: inboxKeys.savedMessages(entityType, entityId),
		enabled: isSupported && !!organizationId && !!entityId,
		staleTime: 30 * 1000,
		queryFn: () => fetchSavedMessages(entityType, entityId, organizationId!),
	});

	return { ...query, isSupported };
}

/** Delete a saved message (link rows first, then the message itself). */
export function useDeleteSavedMessage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (messageId: string) => {
			await Promise.all([
				supabase.from("public_message_candidate").delete().eq("public_message_id", messageId),
				supabase.from("public_message_jobs").delete().eq("public_message_id", messageId),
				supabase.from("public_message_company").delete().eq("public_message_id", messageId),
			]);
			const { error } = await supabase.from("public_messages").delete().eq("id", messageId);
			if (error) throw error;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [...inboxKeys.all, "saved"] });
			toast({
				title: t("inbox.saved.deleteSuccessTitle", "Deleted"),
				description: t("inbox.saved.deleteSuccessDescription", "The saved message was deleted."),
			});
		},
		onError: () => {
			toast({
				title: t("inbox.saved.deleteErrorTitle", "Delete failed"),
				description: t("inbox.saved.deleteErrorDescription", "The saved message could not be deleted."),
				variant: "destructive",
			});
		},
	});
}
